import { useState } from "react";
import { X, ArrowRight, ArrowLeft, CheckCircle, Building, Globe, Factory, Truck, Leaf, ShieldCheck, Info, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CertificationFinder = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    industry: "",
    operation_type: "",
    export_markets: [],
    regulatory_requirements: [],
    current_certifications: [],
    company_size: ""
  });

  const industries = [
    { id: "food_manufacturing", name: "Food Manufacturing", icon: Factory, desc: "Processing, packaging, beverages" },
    { id: "farms_primary_production", name: "Farms & Primary Production", icon: Leaf, desc: "Agriculture, aquaculture, livestock" },
    { id: "packing_cold_storage", name: "Packing & Cold Storage", icon: Truck, desc: "Distribution, warehousing, logistics" },
    { id: "hospitality", name: "Hospitality & Food Service", icon: Building, desc: "Hotels, restaurants, catering" },
    { id: "other_manufacturing", name: "Other Manufacturing", icon: Factory, desc: "Non-food manufacturing" },
    { id: "service_organization", name: "Service Organization", icon: Building, desc: "Professional services, IT, consulting" },
    { id: "government_public_sector", name: "Government & Public Sector", icon: ShieldCheck, desc: "Government agencies, public bodies" },
    { id: "healthcare", name: "Healthcare", icon: ShieldCheck, desc: "Hospitals, clinics, medical services" }
  ];

  const exportMarkets = [
    "United States", "European Union", "United Kingdom", "Canada", "Caribbean (CARICOM)", 
    "Central America", "South America", "Asia Pacific", "Middle East", "Africa", "No Export (Local Only)"
  ];

  const certifications = [
    "ISO 9001", "ISO 22000", "FSSC 22000", "HACCP", "GlobalG.A.P.", "ISO 27001", 
    "ISO 45001", "ISO 14001", "SQF", "BRC", "None"
  ];

  const companySizes = [
    { id: "micro", name: "Micro (1-10 employees)" },
    { id: "small", name: "Small (11-50 employees)" },
    { id: "medium", name: "Medium (51-250 employees)" },
    { id: "large", name: "Large (251+ employees)" }
  ];

  const handleIndustrySelect = (industryId) => {
    setFormData({ ...formData, industry: industryId });
    setStep(2);
  };

  const handleMarketToggle = (market) => {
    const markets = formData.export_markets.includes(market)
      ? formData.export_markets.filter(m => m !== market)
      : [...formData.export_markets, market];
    setFormData({ ...formData, export_markets: markets });
  };

  const handleCertificationToggle = (cert) => {
    const certs = formData.current_certifications.includes(cert)
      ? formData.current_certifications.filter(c => c !== cert)
      : [...formData.current_certifications, cert];
    setFormData({ ...formData, current_certifications: certs });
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/tools/certification-finder`, {
        ...formData,
        operation_type: formData.industry
      });
      setResult(res.data);
      
      // Save to session
      const sessionId = localStorage.getItem("chatbot_session_id");
      if (sessionId) {
        await axios.post(`${API}/tools/session/save`, {
          session_id: sessionId,
          tool_name: "certification_finder",
          result: res.data
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "bg-red-100 text-red-700 border-red-200";
      case "medium": return "bg-amber-100 text-amber-700 border-amber-200";
      case "low": return "bg-green-100 text-green-700 border-green-200";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">What industry are you in?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select the industry that best describes your organization's primary activities.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {industries.map((ind) => (
                <button
                  key={ind.id}
                  onClick={() => handleIndustrySelect(ind.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all hover:border-brand hover:bg-brand/5 ${
                    formData.industry === ind.id ? 'border-brand bg-brand/5' : 'border-slate-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <ind.icon className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <div className="font-medium">{ind.name}</div>
                      <div className="text-xs text-muted-foreground">{ind.desc}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">What markets do you serve or plan to export to?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select all markets that apply. This helps us identify certification requirements.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {exportMarkets.map((market) => (
                <button
                  key={market}
                  onClick={() => handleMarketToggle(market)}
                  className={`px-4 py-2 rounded-full border text-sm transition-all ${
                    formData.export_markets.includes(market)
                      ? 'bg-brand text-white border-brand'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-brand'
                  }`}
                >
                  {market}
                </button>
              ))}
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button onClick={() => setStep(3)} className="bg-brand hover:bg-brand-dark">
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">What certifications do you currently have?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select any certifications your organization currently holds.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {certifications.map((cert) => (
                <button
                  key={cert}
                  onClick={() => handleCertificationToggle(cert)}
                  className={`px-4 py-2 rounded-full border text-sm transition-all ${
                    formData.current_certifications.includes(cert)
                      ? 'bg-brand text-white border-brand'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-brand'
                  }`}
                >
                  {cert}
                </button>
              ))}
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button onClick={() => setStep(4)} className="bg-brand hover:bg-brand-dark">
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">What's your organization size?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                This helps us tailor recommendations to your scale of operations.
              </p>
            </div>
            <div className={`space-y-3 ${errors.company_size ? 'ring-2 ring-red-300 ring-offset-2 rounded-xl' : ''}`}>
              {companySizes.map((size) => (
                <button
                  key={size.id}
                  onClick={() => {
                    setFormData({ ...formData, company_size: size.id });
                    if (errors.company_size) setErrors({...errors, company_size: null});
                  }}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    formData.company_size === size.id 
                      ? 'border-brand bg-brand/5' 
                      : 'border-slate-200 hover:border-brand/50'
                  }`}
                >
                  <div className="font-medium">{size.name}</div>
                </button>
              ))}
            </div>
            {errors.company_size && (
              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg" data-testid="error-company-size">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>{errors.company_size}</span>
              </div>
            )}
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(3)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button 
                onClick={() => {
                  if (!formData.company_size) {
                    setErrors({...errors, company_size: "Please select your organization size to get personalized recommendations"});
                    return;
                  }
                  handleSubmit();
                }} 
                className="bg-brand hover:bg-brand-dark"
                disabled={isLoading}
              >
                {isLoading ? "Analyzing..." : "Get Recommendations"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (result) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-primary">Your Certification Recommendations</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="bg-brand/5 border border-brand/20 rounded-xl p-4 mb-6">
          <p className="text-sm">{result.summary}</p>
        </div>

        <div className="space-y-4 mb-6">
          <h3 className="font-semibold text-lg">Recommended Certifications</h3>
          {result.recommendations.map((rec, index) => (
            <div key={index} className="bg-white border rounded-xl p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-brand" />
                  <span className="font-semibold">{rec.standard}</span>
                </div>
                <Badge className={getPriorityColor(rec.priority)}>
                  {rec.priority} priority
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mb-3">{rec.reason}</p>
              <div className="flex flex-wrap gap-2">
                {rec.benefits.slice(0, 3).map((benefit, i) => (
                  <span key={i} className="text-xs bg-slate-100 px-2 py-1 rounded-full">
                    {benefit}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-slate-50 rounded-xl p-4 mb-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Info className="h-4 w-4 text-brand" />
            Next Steps
          </h3>
          <ul className="space-y-2">
            {result.next_steps.map((step, i) => (
              <li key={i} className="text-sm flex items-start gap-2">
                <span className="text-brand font-bold">{i + 1}.</span>
                {step}
              </li>
            ))}
          </ul>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={() => { setResult(null); setStep(1); }} className="flex-1">
            Start Over
          </Button>
          <Button onClick={onClose} className="flex-1 bg-brand hover:bg-brand-dark">
            Continue to Request Support
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-primary">Certification Finder</h2>
          <p className="text-sm text-muted-foreground">Step {step} of 4</p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-slate-100 rounded-full h-2 mb-8">
        <div 
          className="bg-brand h-2 rounded-full transition-all duration-300"
          style={{ width: `${(step / 4) * 100}%` }}
        />
      </div>

      {renderStep()}
    </div>
  );
};

export default CertificationFinder;
